<template>
    <main
        v-if="data"
        class="relative pb-10 pt-25 md:pb-15 md:pt-36">
        <TheBreadcrumbs
            :items="breadcrumbs"
            class="mb-5" />

        <AtomTitle
            level="1"
            class="container">
            СМИ о нас
        </AtomTitle>

        <LazyApiSection
            recovery-required
            :recovery-ready="!!mediaCompanies && !pending"
            :recovery-transitioning="recoveryTransitioning"
            min-height="32rem"
            @visible="loadDeferredMediaContent">
            <Transition
                @before-leave="recoveryTransitioning = true"
                @after-enter="recoveryTransitioning = false"
                name="fast-fade"
                mode="out-in">
                <section
                    v-if="mediaCompanies?.data.length && !pending"
                    class="mt-10">
                    <div
                        class="container grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                        <MediaPublicationsCard
                            v-for="item in mediaCompanies.data"
                            :key="item.id"
                            :item />
                    </div>
                    <PaginationSection
                        :top-on-scroll="0"
                        show-items-per-page
                        pages-name="записей"
                        :pagination="mediaCompanies.meta.pagination"
                        :items-count="mediaCompanies.data.length"
                        class="mt-10" />
                </section>
                <section
                    v-else-if="!mediaCompanies?.data.length && !pending"
                    class="container relative mt-10 flex items-center justify-center overflow-hidden rounded-20 py-40">
                    <div
                        class="relative space-y-7 text-center text-24/133 font-semibold">
                        <p>Скоро здесь появятся материалы о нашей компании</p>
                    </div>
                    <div class="absolute inset-0 size-full">
                        <div class="bg-noise"></div>
                        <div
                            class="absolute left-0 top-0 aspect-square w-1/3 -translate-x-1/2 -translate-y-1/2 animate-slow-pulse rounded-full bg-accent/60 blur-[6rem] delay-300" />
                        <div
                            class="absolute bottom-0 right-0 aspect-square w-1/3 translate-x-1/2 translate-y-1/2 animate-slow-pulse rounded-full bg-accent/50 blur-[6rem] delay-500" />
                    </div>
                </section>
                <section
                    v-else
                    class="flex h-[50vh] w-full items-center justify-center">
                    <BaseLoader />
                </section>
            </Transition>
        </LazyApiSection>
    </main>
</template>

<script setup lang="ts">
import type { MediaPage } from '~/types/pages.types'

import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import BaseLoader from '~/components/base/base-loader/BaseLoader.vue'
import TheBreadcrumbs, {
    type BreadcrumbItem,
} from '~/components/base/the-breadcrumbs/TheBreadcrumbs.vue'
import MediaPublicationsCard from '~/components/pages/about/media-publications-section/MediaPublicationsCard.vue'
import LazyApiSection from '~/components/particles/lazy-api-section/LazyApiSection.vue'
import PaginationSection from '~/components/particles/pagination-section/PaginationSection.vue'

import type { BaseL5Keys } from '~/composables/useL5'

const nuxtApp = useNuxtApp()
const modalStore = useModalStore()
const recoveryTransitioning = ref(false)

const route = useRoute()

const breadcrumbs: BreadcrumbItem[] = [
    {
        label: 'Главная',
        to: '/',
    },
    {
        label: 'СМИ о нас',
    },
]

function getNumericQueryValue(
    value: typeof route.query.page,
    fallback: number
): number {
    const rawValue = Array.isArray(value) ? value[0] : value
    const numericValue = Number(rawValue)

    return Number.isFinite(numericValue) && numericValue > 0
        ? numericValue
        : fallback
}

const query = reactive<BaseL5Keys>({
    sortedBy: 'desc',
    orderBy: 'id',
    searchJoin: 'and',
    searchFields: '',
    search: '',
    page: getNumericQueryValue(route.query.page, 1),
    limit: getNumericQueryValue(route.query.limit, 15),
})

const deferredQuery = computed(() => ({
    ...query,
    include: ['media_companies'],
}))

const handleUpdateQuery = debounce(() => {
    query.page = getNumericQueryValue(route.query.page, 1)
    query.limit = getNumericQueryValue(route.query.limit, 15)
}, 1000)

watch(() => route.query, handleUpdateQuery)

const { data, error } = await useApiFetch<Page<MediaPage>>(
    `/v1/media-company`,
    {
        query: query,
        getCachedData(key) {
            return nuxtApp.payload?.data?.[key]
        },
        useLastModified: true,
    }
)

const {
    data: deferredMediaData,
    pending: deferredMediaPending,
    execute: executeDeferredMedia,
} = await useApiFetch<Page<MediaPage>>(`/v1/media-company`, {
    key: 'deferred-media-company',
    query: deferredQuery,
    server: false,
    immediate: false,
    watch: false,
    getCachedData(key) {
        return nuxtApp.payload?.data?.[key]
    },
})

const isDeferredMediaContentEnabled = ref(false)

async function loadDeferredMediaContent() {
    if (isDeferredMediaContentEnabled.value) return

    isDeferredMediaContentEnabled.value = true

    await executeDeferredMedia()
}

watch(query, async () => {
    if (!isDeferredMediaContentEnabled.value) return

    await executeDeferredMedia()
})

const mediaCompanies = computed(
    () => deferredMediaData.value?.data.media_companies || null
)
const pending = computed(() => deferredMediaPending.value)

if (error.value) {
    throw createError(
        Object.assign(error.value, {
            message: error.value.statusMessage,
            statusMessage: 'error',
        })
    )
}

if (data.value?.data) {
    modalStore.setFeedbackLabel(data.value.data?.feedback_label || '')
} else {
    modalStore.setFeedbackLabel('')
}

if (data.value) {
    useSeoMeta({
        title: data.value.data.seo.seo_title,
        description: data.value.data.seo.seo_description,
        ogTitle: data.value.data.seo.og_title,
        ogDescription: data.value.data.seo.og_description,
        ogImage: data.value.data.seo.og_image
            ? data.value.data.seo.og_image
            : null,
    })
}
</script>
